package com.swetdot.swetgram.ui.groups

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.swetdot.swetgram.data.groups.GroupRepository
import com.swetdot.swetgram.data.groups.asGroupFailure
import com.swetdot.swetgram.domain.Group
import com.swetdot.swetgram.domain.GroupBanEntry
import com.swetdot.swetgram.domain.GroupFailure
import com.swetdot.swetgram.domain.GroupId
import com.swetdot.swetgram.domain.UserId
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

/**
 * A group's ban list: `listGroupBans` and `unbanGroupMember`.
 *
 * THE ONLY READ SIDE OF A BAN, which is why the surface exists at all. A banned person is not a
 * member, so they are on no member list; without this screen a ban made from a member card is
 * invisible and there is nothing to point `unbanGroupMember` at.
 *
 * NEITHER DOCUMENT DESCRIBES IT. SPS §12.7's table has no ban row and STA §13.5 lists no ban
 * endpoint -- the contract has three. Authority is Owner and Admin, DERIVED from §12.7's removal
 * row the same way the server derives it, and stated as a decision rather than as spec.
 *
 * ORDER IS THE SERVER'S: by user id ascending, because it pages off the primary key. Not a display
 * order anybody would choose and not one the contract sets, so nothing here re-sorts a page -- a
 * client that did would break what the cursor means.
 *
 * UNBANNING DOES NOT RE-ADD, and every piece of copy on this surface has to carry that. No
 * operation in v1.0.0 puts somebody into a group without their asking, so lifting a ban only
 * restores their ability to use a link or file a request. "Unban" reads like "undo" and it is not
 * one.
 */

// ---------------------------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------------------------

data class GroupBansUiState(
    /** Read alongside the first page, for the permission re-check on every unban. */
    val group: Group? = null,
    val bans: List<GroupBanEntry> = emptyList(),
    val loadedOnce: Boolean = false,
    val loadingMore: Boolean = false,
    val hasMore: Boolean = false,
    /** The user currently being unbanned. Only ever one. */
    val unbanning: UserId? = null,
    val error: String? = null,
) {
    /** False until the group is loaded, which grants nothing. */
    val canUnban: Boolean get() = group?.permissions?.canModerate == true
}

/** Follows `GroupJoinRequestsViewModel`, which follows `GroupMembersViewModel`. */
class GroupBansViewModel(
    private val groupId: GroupId,
    private val groups: GroupRepository,
) : ViewModel() {

    private val _state = MutableStateFlow(GroupBansUiState())
    val state: StateFlow<GroupBansUiState> = _state.asStateFlow()

    /** Opaque and server-owned. Never parsed, compared or persisted. */
    private var cursor: String? = null

    private var paging = false
    private var loading = false

    init {
        refresh()
    }

    /** The Retry entry point: a fresh read that also takes the banner down. */
    fun refresh() = reload(clearError = true)

    /** See `GroupJoinRequestsViewModel.reload` for why [clearError] exists. */
    private fun reload(clearError: Boolean) {
        if (loading) return
        loading = true
        if (clearError) _state.value = _state.value.copy(error = null)
        viewModelScope.launch {
            try {
                val group = groups.group(groupId)
                val page = groups.bans(groupId)
                cursor = page.nextCursor
                _state.value = _state.value.copy(
                    group = group,
                    bans = page.items,
                    hasMore = page.hasMore,
                    loadedOnce = true,
                )
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                _state.value = _state.value.copy(
                    loadedOnce = true,
                    error = error.groupBanner("Could not load the banned people."),
                )
            } finally {
                loading = false
            }
        }
    }

    fun loadMore() {
        val current = _state.value
        if (paging || !current.hasMore) return
        val next = cursor ?: return
        paging = true
        _state.value = current.copy(loadingMore = true)
        viewModelScope.launch {
            try {
                val page = groups.bans(groupId, cursor = next)
                cursor = page.nextCursor
                val held = _state.value.bans
                // De-duplicated by user id: the list is keyed by it and `LazyColumn` throws on two
                // items with the same key, which a refresh landing mid-append could produce.
                val known = held.mapTo(mutableSetOf()) { it.user.id }
                _state.value = _state.value.copy(
                    bans = held + page.items.filterNot { it.user.id in known },
                    hasMore = page.hasMore,
                )
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                _state.value = _state.value.copy(
                    error = error.groupBanner("Could not load more banned people."),
                )
            } finally {
                paging = false
                _state.value = _state.value.copy(loadingMore = false)
            }
        }
    }

    /**
     * `unbanGroupMember`. 204.
     *
     * NOT OPTIMISTIC, unlike `ProfileViewModel`'s unblock, and the difference is the same one that
     * governs every act on this pass's surfaces: a block is private to the person who made it, so
     * showing it lifted early costs nobody anything, while a ban is a shared moderation state that
     * a second admin may be looking at in the same second.
     *
     * The permission is re-checked here against the group as last read rather than trusted from the
     * button, because an admin can be demoted while this list is open.
     *
     * A 204 for somebody who was never banned means a second tap is harmless, so a row that is
     * already gone on the server still disappears cleanly here.
     */
    fun unban(userId: UserId) {
        val current = _state.value
        if (current.unbanning != null) return
        if (!current.canUnban) return
        val target = current.bans.firstOrNull { it.user.id == userId } ?: return

        _state.value = current.copy(unbanning = userId, error = null)
        viewModelScope.launch {
            try {
                groups.unbanMember(groupId, userId)
                dropBan(userId)
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                when (error.asGroupFailure()) {
                    GroupFailure.INSUFFICIENT_ROLE -> {
                        _state.value = _state.value.copy(
                            unbanning = null,
                            error = "You do not have permission to lift bans in this group.",
                        )
                        // The role this screen was drawn from is not the server's.
                        reload(clearError = false)
                    }
                    else -> _state.value = _state.value.copy(
                        unbanning = null,
                        error = error.groupBanner(
                            "Could not unban ${target.user.displayName}.",
                        ),
                    )
                }
            }
        }
    }

    private fun dropBan(userId: UserId) {
        _state.value = _state.value.copy(
            unbanning = null,
            bans = _state.value.bans.filterNot { it.user.id == userId },
        )
    }

    fun dismissError() {
        _state.value = _state.value.copy(error = null)
    }
}
